// Transaction API endpoints using RTK Query

import { baseApi } from './baseApi'
import { API_ENDPOINTS } from '@/constants'
import type {
  Transaction,
  CreateTransactionData,
  UpdateTransactionData,
  TransactionFilters,
  FinancialSummary,
  ApiResponse,
  PaginatedResponse,
} from '@/types'

export const transactionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all transactions with filters
    getTransactions: builder.query<PaginatedResponse<Transaction>, TransactionFilters | void>({
      query: (filters) => ({
        url: API_ENDPOINTS.TRANSACTIONS.BASE,
        params: filters || {},
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<Transaction>>) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Transaction' as const, id })),
              { type: 'Transaction', id: 'LIST' },
            ]
          : [{ type: 'Transaction', id: 'LIST' }],
    }),

    // Get transaction by ID
    getTransactionById: builder.query<Transaction, string>({
      query: (id) => API_ENDPOINTS.TRANSACTIONS.DETAIL(id),
      transformResponse: (response: ApiResponse<Transaction>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Transaction', id }],
    }),

    // Get transactions by farm ID
    getTransactionsByFarm: builder.query<Transaction[], string>({
      query: (farmId) => API_ENDPOINTS.TRANSACTIONS.BY_FARM(farmId),
      transformResponse: (response: ApiResponse<Transaction[]>) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Transaction' as const, id })),
              { type: 'Transaction', id: 'LIST' },
            ]
          : [{ type: 'Transaction', id: 'LIST' }],
    }),

    // Create transaction
    createTransaction: builder.mutation<Transaction, CreateTransactionData>({
      query: (data) => ({
        url: API_ENDPOINTS.TRANSACTIONS.CREATE,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<Transaction>) => response.data,
      invalidatesTags: [{ type: 'Transaction', id: 'LIST' }],
    }),

    // Update transaction
    updateTransaction: builder.mutation<Transaction, { id: string; data: UpdateTransactionData }>({
      query: ({ id, data }) => ({
        url: API_ENDPOINTS.TRANSACTIONS.UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<Transaction>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Transaction', id },
        { type: 'Transaction', id: 'LIST' },
      ],
    }),

    // Delete transaction
    deleteTransaction: builder.mutation<void, string>({
      query: (id) => ({
        url: API_ENDPOINTS.TRANSACTIONS.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Transaction', id },
        { type: 'Transaction', id: 'LIST' },
      ],
    }),

    // Get financial summary
    getFinancialSummary: builder.query<FinancialSummary, TransactionFilters | void>({
      query: (filters) => ({
        url: API_ENDPOINTS.TRANSACTIONS.SUMMARY,
        params: filters || {},
      }),
      transformResponse: (response: ApiResponse<FinancialSummary>) => response.data,
      providesTags: ['Transaction'],
    }),
  }),
})

export const {
  useGetTransactionsQuery,
  useGetTransactionByIdQuery,
  useGetTransactionsByFarmQuery,
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
  useDeleteTransactionMutation,
  useGetFinancialSummaryQuery,
} = transactionApi
